import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { User, Phone, Envelope, WhatsappLogo, ToggleRight, ToggleLeft, Eye, EyeSlash, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (val) => {
  if (!val) return '';
  if (/^https?:\/\//i.test(val)) return val;
  return `${API_URL}${val}`;
};

const HELPLINE_META = {
  phone:    { label: 'Helpline Phone',  placeholder: '+91 1800-XXX-XXX', icon: Phone },
  email:    { label: 'Helpline Email',  placeholder: 'help@yourdomain.com', icon: Envelope },
  whatsapp: { label: 'WhatsApp Number', placeholder: '+91 9XXXXXXXXX', icon: WhatsappLogo },
};

const ProfileHelplineView = () => {
  const [profile, setProfile] = useState({ name: '', phone: '', address: '', city: '', state: '', country: '', pincode: '', profile_photo: '' });
  const [helpline, setHelpline] = useState({
    phone:    { value: '', is_active: false, show_to_public: false },
    email:    { value: '', is_active: false, show_to_public: false },
    whatsapp: { value: '', is_active: false, show_to_public: false },
  });
  const [savingP, setSavingP] = useState(false);
  const [savingH, setSavingH] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/profile/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(p => ({ ...p, profile_photo: res.data?.profile_photo || p.profile_photo }));
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const fetchAll = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([
        axios.get(`${API_URL}/api/admin/profile`),
        axios.get(`${API_URL}/api/admin/helpline`),
      ]);
      const pd = p.data || {};
      setProfile({
        name: pd.name || '', phone: pd.phone || '', address: pd.address || '',
        city: pd.city || '', state: pd.state || '', country: pd.country || '',
        pincode: pd.pincode || '', profile_photo: pd.profile_photo || '',
      });
      setHelpline(h.data?.helpline || helpline);
    } catch (e) { toast.error('Could not load admin profile / helpline'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveProfile = async () => {
    if (!profile.name?.trim()) return toast.error('Name is required');
    setSavingP(true);
    try {
      await axios.put(`${API_URL}/api/admin/profile`, profile);
      toast.success('Profile saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingP(false); }
  };

  const saveHelpline = async () => {
    setSavingH(true);
    try {
      await axios.put(`${API_URL}/api/admin/helpline`, { helpline });
      toast.success('Helpline updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingH(false); }
  };

  const updHelpline = (key, field, val) => {
    setHelpline(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }));
  };

  return (
    <div data-testid="admin-profile-helpline" className="space-y-6">
      <p className="text-[#6b7a99] text-sm">Edit your admin profile and manage helpline contacts shown to buyers & sellers</p>

      {/* Profile card */}
      <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-5" data-testid="admin-profile-card">
        <h3 className="text-white text-sm font-semibold inline-flex items-center gap-2 mb-4">
          <User size={16} className="text-indigo-400" /> Admin Profile
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Full name *">
            <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder="e.g. Look4Space Admin" className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="admin-name-input" />
          </Field>
          <Field label="Phone">
            <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              placeholder="+91 9XXXXXXXXX" className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="admin-phone-input" />
          </Field>
          <Field label="Address" full>
            <Input value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })}
              placeholder="Street, locality" className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="admin-address-input" />
          </Field>
          <Field label="City">
            <Input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })}
              className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
          </Field>
          <Field label="State">
            <Input value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })}
              className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
          </Field>
          <Field label="Country">
            <Input value={profile.country} onChange={(e) => setProfile({ ...profile, country: e.target.value })}
              className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
          </Field>
          <Field label="Pincode / ZIP">
            <Input value={profile.pincode} onChange={(e) => setProfile({ ...profile, pincode: e.target.value })}
              className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
          </Field>
          <Field label="Profile photo" full>
            <div className="flex items-center gap-3" data-testid="profile-photo-field">
              {profile.profile_photo ? (
                <img src={photoSrc(profile.profile_photo)} alt="Admin"
                  className="w-14 h-14 rounded-full object-cover border border-[#252d3d]" data-testid="profile-photo-preview" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#0f1320] border border-[#252d3d] flex items-center justify-center text-[#6b7a99]">
                  <User size={22} />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <div className="flex gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-indigo-500/30 text-indigo-300 text-xs font-medium cursor-pointer bg-[#0f1320] hover:bg-[#161b26]" data-testid="upload-photo-label">
                    <UploadSimple size={14} />
                    {uploading ? 'Uploading…' : 'Upload Image'}
                    <input type="file" accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => uploadPhoto(e.target.files?.[0])}
                      disabled={uploading}
                      className="hidden" data-testid="upload-photo-input" />
                  </label>
                  {profile.profile_photo && (
                    <Button size="sm" variant="ghost" onClick={() => setProfile(p => ({ ...p, profile_photo: '' }))}
                      className="text-red-300 hover:text-red-200 hover:bg-red-500/10 h-9 rounded-lg text-xs" data-testid="remove-photo-btn">
                      Remove
                    </Button>
                  )}
                </div>
                <Input value={profile.profile_photo} onChange={(e) => setProfile({ ...profile, profile_photo: e.target.value })}
                  placeholder="Or paste image URL..." className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="profile-photo-url-input" />
              </div>
            </div>
          </Field>
        </div>
        <div className="flex justify-end mt-4">
          <Button size="sm" disabled={savingP} onClick={saveProfile} className="bg-emerald-600 hover:bg-emerald-700 h-9 rounded-lg text-xs" data-testid="save-admin-profile">
            {savingP ? 'Saving…' : 'Save Profile'}
          </Button>
        </div>
      </div>

      {/* Helpline card */}
      <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-5" data-testid="helpline-card">
        <h3 className="text-white text-sm font-semibold mb-1">Helpline Contacts</h3>
        <p className="text-[11px] text-[#6b7a99] mb-4">Each channel has its own switches: <strong>Active</strong> means it&apos;s configured, <strong>Show to Public</strong> means buyers/sellers see it in the platform footer.</p>
        <div className="space-y-3">
          {Object.entries(HELPLINE_META).map(([key, meta]) => {
            const cur = helpline[key] || { value: '', is_active: false, show_to_public: false };
            const Icon = meta.icon;
            return (
              <div key={key} data-testid={`helpline-row-${key}`} className="bg-[#0f1320] border border-[#252d3d] rounded-lg p-3 flex flex-wrap gap-3 items-center">
                <div className="inline-flex items-center gap-2 min-w-[180px]">
                  <Icon size={18} className="text-indigo-400" />
                  <span className="text-white text-sm">{meta.label}</span>
                </div>
                <Input value={cur.value} onChange={(e) => updHelpline(key, 'value', e.target.value)}
                  placeholder={meta.placeholder}
                  className="flex-1 min-w-[180px] h-9 bg-[#161b26] border-[#252d3d] text-white text-sm" data-testid={`helpline-value-${key}`} />
                <button onClick={() => updHelpline(key, 'is_active', !cur.is_active)}
                  className="inline-flex items-center gap-1 text-xs text-[#8896b0] hover:text-white px-2" data-testid={`helpline-active-${key}`}>
                  {cur.is_active ? <ToggleRight size={20} weight="fill" className="text-emerald-400" /> : <ToggleLeft size={20} />}
                  <span className="text-[11px]">Active</span>
                </button>
                <button onClick={() => updHelpline(key, 'show_to_public', !cur.show_to_public)}
                  className="inline-flex items-center gap-1 text-xs text-[#8896b0] hover:text-white px-2" data-testid={`helpline-show-${key}`}>
                  {cur.show_to_public ? <Eye size={18} className="text-blue-400" weight="fill" /> : <EyeSlash size={18} />}
                  <span className="text-[11px]">Show to public</span>
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end mt-4">
          <Button size="sm" disabled={savingH} onClick={saveHelpline} className="bg-emerald-600 hover:bg-emerald-700 h-9 rounded-lg text-xs" data-testid="save-helpline">
            {savingH ? 'Saving…' : 'Save Helpline'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="text-[10px] text-[#6b7a99] uppercase tracking-wider block mb-1">{label}</label>
    {children}
  </div>
);

export default ProfileHelplineView;
